import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getAuthProfile,
  getDemoLoginCredentials,
  getDemoRegistrationCredentials,
  isAuthenticated as serviceIsAuthenticated,
  login as serviceLogin,
  loginWithProvider as serviceLoginWithProvider,
  logout as serviceLogout,
  onAuthStateChange,
  register as serviceRegister,
  resendConfirmationEmail as serviceResendConfirmationEmail,
} from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [authPending, setAuthPending] = useState(false);
  const [error, setError] = useState(null);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState(null);

  const refreshAuthState = useCallback(async () => {
    try {
      const authenticated = await serviceIsAuthenticated();
      if (!authenticated) {
        setUser(null);
        return null;
      }

      const profile = await getAuthProfile();
      setUser(profile);
      return profile;
    } catch (err) {
      console.error('Failed to refresh auth state:', err);
      setError(err);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const initialize = async () => {
      setInitializing(true);
      await refreshAuthState();
      setInitializing(false);
    };

    initialize();
  }, [refreshAuthState]);

  useEffect(() => {
    const { data: subscription } = onAuthStateChange(() => {
      refreshAuthState();
    });

    return () => {
      subscription?.subscription?.unsubscribe();
    };
  }, [refreshAuthState]);

  const handleAuthOperation = useCallback(async (operation) => {
    setAuthPending(true);
    setError(null);

    try {
      const result = await operation();
      await refreshAuthState();
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setAuthPending(false);
    }
  }, [refreshAuthState]);

  const login = useCallback(async (credentials) => {
    try {
      const result = await handleAuthOperation(() => serviceLogin(credentials));
      setPendingVerificationEmail(null);
      return result;
    } catch (err) {
      if (err?.message?.toLowerCase?.().includes('email not confirmed')) {
        setPendingVerificationEmail(credentials?.email);
      }
      throw err;
    }
  }, [handleAuthOperation]);

  const loginWithProvider = useCallback((provider) => {
    return handleAuthOperation(() => serviceLoginWithProvider(provider));
  }, [handleAuthOperation]);

  const register = useCallback(async (data, options) => {
    const result = await handleAuthOperation(() => serviceRegister(data, options));
    if (result?.emailConfirmationRequired) {
      setPendingVerificationEmail(result?.user?.email || data?.email);
    } else {
      setPendingVerificationEmail(null);
    }
    return result;
  }, [handleAuthOperation]);

  const logout = useCallback(() => {
    return handleAuthOperation(() => serviceLogout());
  }, [handleAuthOperation]);

  const resendConfirmationEmail = useCallback(async (email) => {
    setAuthPending(true);
    setError(null);

    try {
      return await serviceResendConfirmationEmail(email);
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setAuthPending(false);
    }
  }, []);

  const value = useMemo(() => ({
    user,
    initializing,
    authPending,
    error,
    login,
    loginWithProvider,
    register,
    logout,
    refreshAuthState,
    clearError: () => setError(null),
    isAuthenticated: Boolean(user),
    demoLoginCredentials: getDemoLoginCredentials(),
    demoRegistrationCredentials: getDemoRegistrationCredentials(),
    pendingVerificationEmail,
    resendConfirmationEmail,
  }), [authPending, error, login, loginWithProvider, logout, register, resendConfirmationEmail, user, initializing, refreshAuthState, pendingVerificationEmail]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;
